"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import {
  Search,
  X,
  ArrowRight,
  Target,
  Briefcase,
  Map,
  Layout,
} from "lucide-react";
import { useNavigate } from "react-router";
import { StudentStore } from "@/hooks/stores/StudentStore";
import { AuthStore } from "@/hooks/stores/AuthStore";
import { StorageService } from "@/hooks/services/storageService";
import { getAllNodes } from "@/data/career-tree";

interface SearchResult {
  id: string;
  title: string;
  description?: string;
  category: "Goal" | "Career" | "Path" | "Assessment" | "Page";
  url: string;
  icon: React.ReactNode;
}

interface UniversalSearchProps {
  onSearch?: (query: string) => void;
}

export function UniversalSearch({ onSearch }: UniversalSearchProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);

  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Real data from stores
  const student = StudentStore((s) => s.student);
  const userId = AuthStore((s) => s.currentUser?._id);

  const { careerInterests, selectedBroadPaths } = useMemo(() => ({
    careerInterests: student?.careerInterests ?? [],
    selectedBroadPaths: student?.selectedBroadPaths ?? [],
  }), [student]);

  const goals = useMemo(() => {
    if (!userId) return [];
    const all = StorageService.read("goals") as Array<{ id: string; userId: string; title: string; progress?: number }> | null;
    return Array.isArray(all) ? all.filter((g) => g.userId === userId) : [];
  }, [userId]);

  const allNodes = useMemo(() => getAllNodes(), []);

  // 1. Search Logic Function
  const performSearch = useMemo(() => {
    return (query: string): SearchResult[] => {
      const q = query.toLowerCase().trim();
      if (!q) return [];

      const allResults: SearchResult[] = [];

      // Search student goals (real data)
      goals.forEach((g) => {
        if (g.title.toLowerCase().includes(q)) {
          allResults.push({
            id: g.id,
            title: g.title,
            description: `Goal • ${g.progress ?? 0}% completed`,
            category: "Goal",
            url: "/goals",
            icon: <Target className="w-4 h-4 text-blue-500" />,
          });
        }
      });

      // Search career interests (real data)
      careerInterests.forEach((name) => {
        if (name.toLowerCase().includes(q)) {
          allResults.push({
            id: `career-${name}`,
            title: name,
            description: "Career interest",
            category: "Career",
            url: "/career-explorer",
            icon: <Briefcase className="w-4 h-4 text-amber-500" />,
          });
        }
      });

      // Search selected career paths (real data, resolve ID → name)
      selectedBroadPaths.forEach((pathId) => {
        const node = allNodes.find((n) => n.nodeId === pathId);
        const name = node?.name ?? pathId;
        if (name.toLowerCase().includes(q)) {
          allResults.push({
            id: pathId,
            title: name,
            description: "Your career path",
            category: "Path",
            url: `/career-learning-path?id=${pathId}`,
            icon: <Map className="w-4 h-4 text-emerald-500" />,
          });
        }
      });

      // Search static app pages
      const pages = [
        { title: "Dashboard",          url: "/dashboard" },
        { title: "Career Explorer",    url: "/career-explorer" },
        { title: "Career Path",        url: "/career-path" },
        // { title: "Learning Journey", url: "/journey" }, // hidden — content not ready
        { title: "Achievements",       url: "/achievements" },
        { title: "Goals",              url: "/goals" },
        { title: "AI Companion",       url: "/ai-companion" },
        { title: "Settings",           url: "/settings" },
        { title: "Profile",            url: "/profile" },
      ];

      pages.forEach((page) => {
        if (page.title.toLowerCase().includes(q)) {
          allResults.push({
            id: page.url,
            title: page.title,
            category: "Page",
            url: page.url,
            icon: <Layout className="w-4 h-4 text-purple-500" />,
          });
        }
      });

      return allResults.slice(0, 7);
    };
  }, [goals, careerInterests, selectedBroadPaths, allNodes]);

  // 2. Handle Real-time filtering with Debounce
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchQuery.length > 1) {
        setResults(performSearch(searchQuery));
      } else {
        setResults([]);
      }
    }, 150);
    return () => clearTimeout(handler);
  }, [searchQuery, performSearch]);

  // 3. UI Interaction Handlers
  useEffect(() => {
    if (isExpanded && searchInputRef.current) searchInputRef.current.focus();
  }, [isExpanded]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        if (searchQuery.trim() === "") setIsExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchQuery]);

  const handleResultClick = (url: string) => {
    navigate(url);
    setIsExpanded(false);
    setSearchQuery("");
  };

  const handleCloseSearch = () => {
    setSearchQuery("");
    setIsExpanded(false);
  };

  return (
    <div ref={searchContainerRef} className="fixed top-12 right-6 z-50">
      {/* Search Container */}
      <div
        className={`
          flex items-center bg-teal-600 border border-teal-500 rounded-lg shadow-lg
          transition-all duration-300 ease-in-out
          ${isExpanded ? "w-80 px-4" : "w-12 px-0"}
        `}
      >
        <button
          onClick={() => setIsExpanded(true)}
          className={`flex items-center justify-center shrink-0 ${isExpanded ? "w-6 h-12 mr-3" : "w-12 h-12"} text-white`}
        >
          <Search className="w-6 h-6" />
        </button>

        {isExpanded && (
          <div className="flex-1 flex items-center">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search goals, careers, paths..."
              className="w-full h-12 bg-transparent border-0 focus:outline-none focus:ring-0 text-white placeholder-teal-100/70 text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="ml-2 text-teal-100 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={handleCloseSearch}
              className="ml-4 text-teal-100 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Real-time Results Dropdown */}
      {isExpanded && searchQuery.length > 1 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-2">
            {results.length > 0 ? (
              <>
                <div className="px-3 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Quick Results
                </div>
                <div className="space-y-1">
                  {results.map((result) => (
                    <button
                      key={result.id}
                      onClick={() => handleResultClick(result.url)}
                      className="w-full flex items-center justify-between p-3 hover:bg-teal-50 rounded-lg group transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="shrink-0 p-2 bg-gray-50 rounded-md group-hover:bg-white transition-colors">
                          {result.icon}
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-semibold text-gray-700 truncate group-hover:text-teal-700">
                            {result.title}
                          </span>
                          <span className="text-xs text-gray-400 truncate">
                            {result.description || result.category}
                          </span>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" />
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="p-8 text-center">
                <p className="text-sm text-gray-500 italic">
                  No matches for "{searchQuery}"
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
